import React, { useEffect, useMemo, useState } from "react";
import { CsvButton, Delta, Skeleton, describeDevice, downloadCsv, formatChf, formatDate, formatDateTime, formatRelative } from "./adminUi";

const TABS = [
  { key: "revenue",  label: "Revenue" },
  { key: "payments", label: "Payments" },
  { key: "renewals", label: "Renewals & invoices" },
  { key: "logins",   label: "Logins" },
];

const money = (n) => (n == null ? "—" : formatChf(n, 2));
const monthLabel = (key) => new Date(`${key}-01T12:00:00Z`).toLocaleDateString("en-GB", { month: "short", year: "numeric" });

function Customer({ row, onOpenUser }) {
  const label = row.name || row.email || row.customerId || "Unknown";
  return (
    <>
      {row.userId ? <button type="button" className="adm-link" onClick={() => onOpenUser(row.userId)}>{label}</button> : <span>{label}</span>}
      {!row.hasAccount && <span className="adm-tag adm-tag--warn" title="This Stripe customer has no MTT account">no account</span>}
      {row.name && row.email && <span className="adm-user__email">{row.email}</span>}
    </>
  );
}

// ─── Revenue ──────────────────────────────────────────────────────────────────

function Metric({ label, value, sub, delta }) {
  return (
    <div className="adm-metric">
      <span className="adm-kpi__label">{label}</span>
      <span className="adm-metric__value">{value}{delta}</span>
      {sub && <span className="adm-kpi__sub">{sub}</span>}
    </div>
  );
}

function RevenueTab({ finance }) {
  const s = finance.summary;
  const withFees = finance.source === "stripe";
  const months = [...finance.months].reverse(); // newest first
  const total = finance.months.reduce((t, m) => ({
    payments: t.payments + m.payments, gross: t.gross + m.gross, refunds: t.refunds + m.refunds,
    fees: t.fees + (m.fees || 0), net: t.net + (m.net || 0), newMrr: t.newMrr + m.newMrr, churnedMrr: t.churnedMrr + m.churnedMrr,
  }), { payments: 0, gross: 0, refunds: 0, fees: 0, net: 0, newMrr: 0, churnedMrr: 0 });

  const exportCsv = () => downloadCsv("mtt-revenue-by-month.csv",
    ["Month", "Payments", "Gross CHF", "Refunds CHF", "Fees CHF", "Net CHF", "New MRR CHF", "Churned MRR CHF", "Net new MRR CHF", "MRR CHF", "Paying customers", "ARPU CHF"],
    finance.months.map((m) => [m.month, m.payments, m.gross, m.refunds, m.fees, m.net, m.newMrr, m.churnedMrr, m.netNewMrr, m.mrr, m.payingCustomers, m.arpu]));

  return (
    <>
      <div className="adm-metrics">
        <Metric label={withFees ? "MRR · billed in Stripe" : "MRR"} value={money(s.mrr)}
                sub={`${s.payingCustomers} paying customer${s.payingCustomers === 1 ? "" : "s"}${s.orphanSubscriptions ? ` · ${s.orphanSubscriptions} without account` : ""}`} />
        <Metric label="ARR" value={money(s.arr)} sub="MRR × 12" />
        <Metric label="ARPU" value={money(s.arpu)} sub="per paying customer and month" />
        <Metric label="Net revenue · 30 d" value={money(withFees ? s.last30.net : s.last30.gross)}
                delta={<Delta value={withFees ? s.last30.net : s.last30.gross} previous={withFees ? s.previous30.net : s.previous30.gross} format={(v) => money(v)} />}
                sub={`gross ${money(s.last30.gross)} · ${s.last30.payments} payment${s.last30.payments === 1 ? "" : "s"}${s.last30.failed ? ` · ${s.last30.failed} failed` : ""}`} />
        <Metric label="Revenue · 12 months" value={money(withFees ? s.last12m.net : s.last12m.gross)}
                sub={withFees ? `gross ${money(s.last12m.gross)} · year to date ${money(s.ytd.net)} net` : "gross, from the payment log"} />
        <Metric label="Stripe fees · 12 months" value={withFees ? money(s.last12m.fees) : "—"}
                sub={s.feeRate != null ? `${s.feeRate} % of gross` : "needs Stripe"} />
        <Metric label="Revenue per customer" value={money(s.revenuePerCustomer)} sub="gross, last 12 months" />
        <Metric label="Predicted LTV" value={s.ltv != null ? money(s.ltv) : "—"}
                sub={s.ltv != null ? `ARPU ÷ ${s.monthlyChurnRate} % monthly churn` : "needs churned customers to estimate"} />
        <Metric label="Expected · next 30 d" value={s.expectedNext30 ? money(s.expectedNext30.amount) : "—"}
                sub={s.expectedNext30 ? `${s.expectedNext30.renewals} renewal${s.expectedNext30.renewals === 1 ? "" : "s"} due` : "needs Stripe"} />
        <Metric label="Open invoices" value={money(s.openInvoices.amount)} sub={`${s.openInvoices.count} unpaid`} />
      </div>

      <div className="adm-list-head">
        <h3 className="adm-panel__title">Revenue by month <small>cash basis, CHF</small></h3>
        <CsvButton onClick={exportCsv} />
      </div>
      <div className="adm-table-wrap">
        <table className="adm-table adm-table--static">
          <thead>
            <tr>
              <th>Month</th><th className="is-numeric">Payments</th><th className="is-numeric">Gross</th><th className="is-numeric">Refunds</th>
              <th className="is-numeric">Fees</th><th className="is-numeric">Net</th><th className="is-numeric">New MRR</th>
              <th className="is-numeric">Churned MRR</th><th className="is-numeric">Net new MRR</th><th className="is-numeric">MRR</th>
              <th className="is-numeric">Paying</th><th className="is-numeric">ARPU</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m) => (
              <tr key={m.month} className={m.gross || m.mrr ? "" : "is-quiet"}>
                <td>{monthLabel(m.month)}</td>
                <td className="is-numeric">{m.payments}</td>
                <td className="is-numeric">{money(m.gross)}</td>
                <td className="is-numeric">{m.refunds ? `−${money(m.refunds)}` : "—"}</td>
                <td className="is-numeric">{m.fees == null ? "—" : m.fees ? `−${money(m.fees)}` : "—"}</td>
                <td className="is-numeric adm-strong">{money(m.net)}</td>
                <td className="is-numeric adm-text-accent">{m.newMrr ? `+${money(m.newMrr)}` : "—"}</td>
                <td className="is-numeric adm-text-danger">{m.churnedMrr ? `−${money(m.churnedMrr)}` : "—"}</td>
                <td className={`is-numeric ${m.netNewMrr > 0 ? "adm-text-accent" : m.netNewMrr < 0 ? "adm-text-danger" : ""}`}>{m.netNewMrr ? `${m.netNewMrr > 0 ? "+" : "−"}${money(Math.abs(m.netNewMrr))}` : "—"}</td>
                <td className="is-numeric">{money(m.mrr)}</td>
                <td className="is-numeric">{m.payingCustomers}</td>
                <td className="is-numeric">{money(m.arpu)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>12 months</td>
              <td className="is-numeric">{total.payments}</td>
              <td className="is-numeric">{money(total.gross)}</td>
              <td className="is-numeric">{total.refunds ? `−${money(total.refunds)}` : "—"}</td>
              <td className="is-numeric">{withFees ? `−${money(total.fees)}` : "—"}</td>
              <td className="is-numeric adm-strong">{withFees ? money(total.net) : "—"}</td>
              <td className="is-numeric">{total.newMrr ? `+${money(total.newMrr)}` : "—"}</td>
              <td className="is-numeric">{total.churnedMrr ? `−${money(total.churnedMrr)}` : "—"}</td>
              <td colSpan={4} />
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="adm-note">
        Gross, refunds, fees and net come from {withFees ? "Stripe" : "the payment log"}; MRR movements, paying customers
        and ARPU from MTT's subscription history (existing accounts). Amounts include VAT, if any; discounts aren't separated.
      </p>
    </>
  );
}

// ─── Payments ─────────────────────────────────────────────────────────────────

const PAYMENT_FILTERS = [
  { key: "all", label: "All", test: () => true },
  { key: "succeeded", label: "Paid", test: (p) => p.status === "succeeded" || p.status === "partially_refunded" },
  { key: "failed", label: "Failed", test: (p) => p.status === "failed" },
  { key: "refunded", label: "Refunded", test: (p) => p.refunded > 0 },
];
const PAYMENT_STATUS = { succeeded: "Paid", failed: "Failed", refunded: "Refunded", partially_refunded: "Partly refunded", pending: "Pending" };

function PaymentsTab({ finance, auth, onOpenUser }) {
  const [filter, setFilter] = useState("all");
  const [downloading, setDownloading] = useState(null);
  const [error, setError] = useState(null);
  const withFees = finance.source === "stripe";
  const rows = finance.payments.filter(PAYMENT_FILTERS.find((f) => f.key === filter).test);

  const downloadInvoice = async (p) => {
    setDownloading(p.invoiceId);
    setError(null);
    try {
      const res = await fetch(`/api/admin/invoices/${p.invoiceId}/pdf`, { headers: { Authorization: "Bearer " + auth.token } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || "Download failed.");
      const url = URL.createObjectURL(await res.blob());
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice-${p.invoiceNumber || p.invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(null);
    }
  };

  const exportCsv = () => downloadCsv("mtt-payments.csv",
    ["Date", "Customer", "Email", "Status", "Gross CHF", "Refunded CHF", "Fee CHF", "Net CHF", "Invoice", "Failure reason"],
    rows.map((p) => [p.at, p.name, p.email, p.status, p.gross || p.attempted, p.refunded, p.fee, p.net, p.invoiceNumber, p.failureMessage]));

  return (
    <>
      <div className="adm-list-head">
        <div className="adm-filters" role="group" aria-label="Filter payments">
          {PAYMENT_FILTERS.map((f) => (
            <button key={f.key} type="button" aria-pressed={filter === f.key} className={`adm-filter ${filter === f.key ? "is-active" : ""}`} onClick={() => setFilter(f.key)}>
              {f.label} <span>{finance.payments.filter(f.test).length}</span>
            </button>
          ))}
        </div>
        <CsvButton onClick={exportCsv} />
      </div>
      {error && <p className="adm-note adm-text-danger" role="alert">{error}</p>}
      <div className="adm-table-wrap">
        <table className="adm-table adm-table--static">
          <thead>
            <tr>
              <th>Date</th><th>Customer</th><th>Status</th><th className="is-numeric">Gross</th>
              <th className="is-numeric">Fee</th><th className="is-numeric">Net</th><th>Invoice</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="adm-empty">No payments.</td></tr>}
            {rows.map((p) => (
              <tr key={p.id}>
                <td title={formatDateTime(p.at)}>{formatDate(p.at)}</td>
                <td className="adm-wrap"><Customer row={p} onOpenUser={onOpenUser} /></td>
                <td>
                  <span className={`adm-chip adm-chip--pay-${p.status}`}>{PAYMENT_STATUS[p.status] || p.status}</span>
                  {p.failureMessage && <span className="adm-user__email">{p.failureMessage}</span>}
                  {p.refunded > 0 && p.status !== "refunded" && <span className="adm-user__email">−{money(p.refunded)} refunded</span>}
                </td>
                <td className="is-numeric">{money(p.status === "failed" ? p.attempted : p.gross)}</td>
                <td className="is-numeric">{withFees && p.fee ? `−${money(p.fee)}` : "—"}</td>
                <td className="is-numeric adm-strong">{withFees && p.gross ? money(p.net - p.refunded) : "—"}</td>
                <td>
                  {p.invoiceId ? (
                    <button type="button" className="adm-link" disabled={downloading === p.invoiceId} onClick={() => downloadInvoice(p)}>
                      {downloading === p.invoiceId ? "Preparing…" : p.invoiceNumber || "PDF"}
                    </button>
                  ) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="adm-note">The latest {finance.payments.length} payments{withFees ? " from Stripe" : ""}. Net is after Stripe fees and refunds.</p>
    </>
  );
}

// ─── Renewals and open invoices ───────────────────────────────────────────────

function RenewalsTab({ finance, onOpenUser }) {
  const s = finance.summary;
  if (finance.source !== "stripe") return <p className="adm-note">Renewals and open invoices need Stripe. {finance.warning}</p>;
  const exportCsv = () => downloadCsv("mtt-renewals.csv",
    ["Renews", "Customer", "Email", "Amount CHF", "Status", "Cancels at period end"],
    finance.renewals.map((r) => [r.renewsAt, r.name, r.email, r.amount, r.status, r.cancelAtPeriodEnd ? "yes" : "no"]));

  return (
    <>
      {(s.orphanSubscriptions > 0 || finance.duplicates.length > 0) && (
        <ul className="adm-attention">
          {s.orphanSubscriptions > 0 && (
            <li className="adm-attention__item adm-attention__item--warning"><span className="adm-attention__icon" aria-hidden="true">!</span>
              <span>{s.orphanSubscriptions} active Stripe subscription{s.orphanSubscriptions > 1 ? "s belong" : " belongs"} to customers without an MTT account. They're billed but can't use the service.</span></li>
          )}
          {finance.duplicates.map((d) => (
            <li key={d.userId || d.customerId} className="adm-attention__item adm-attention__item--warning"><span className="adm-attention__icon" aria-hidden="true">!</span>
              <span>{d.name || d.email || d.customerId} has {d.subscriptions} active subscriptions and is billed {d.subscriptions} times.</span></li>
          ))}
        </ul>
      )}

      <div className="adm-list-head">
        <h3 className="adm-panel__title">Upcoming renewals <small>next 60 days · {money(s.expectedNext30.amount)} expected in 30 days</small></h3>
        <CsvButton onClick={exportCsv} />
      </div>
      <div className="adm-table-wrap">
        <table className="adm-table adm-table--static">
          <thead><tr><th>Renews</th><th>Customer</th><th className="is-numeric">Amount</th><th>Status</th></tr></thead>
          <tbody>
            {finance.renewals.length === 0 && <tr><td colSpan={4} className="adm-empty">No renewals in the next 60 days.</td></tr>}
            {finance.renewals.map((r) => (
              <tr key={r.subscriptionId}>
                <td title={formatDateTime(r.renewsAt)}>{formatDate(r.renewsAt)} <span className="adm-user__email">in {Math.max(0, Math.round((Date.parse(r.renewsAt) - Date.now()) / 864e5))} d</span></td>
                <td className="adm-wrap"><Customer row={r} onOpenUser={onOpenUser} /></td>
                <td className="is-numeric">{money(r.amount)}</td>
                <td>
                  {r.cancelAtPeriodEnd ? <span className="adm-chip adm-chip--expired">Cancels</span>
                    : r.status === "past_due" ? <span className="adm-chip adm-chip--complimentary">Past due</span>
                    : r.status === "trialing" ? <span className="adm-chip adm-chip--ending">Trial ends</span>
                    : <span className="adm-chip adm-chip--active">Renews</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="adm-panel__title adm-list-gap">Open invoices <small>{s.openInvoices.count} · {money(s.openInvoices.amount)} outstanding</small></h3>
      <div className="adm-table-wrap">
        <table className="adm-table adm-table--static">
          <thead><tr><th>Invoice</th><th>Customer</th><th className="is-numeric">Due</th><th>Attempts</th><th>Next attempt</th><th /></tr></thead>
          <tbody>
            {finance.openInvoices.length === 0 && <tr><td colSpan={6} className="adm-empty">No open invoices.</td></tr>}
            {finance.openInvoices.map((i) => (
              <tr key={i.id}>
                <td>{i.number || i.id}<span className="adm-user__email">{formatDate(i.createdAt)}</span></td>
                <td className="adm-wrap"><Customer row={i} onOpenUser={onOpenUser} /></td>
                <td className="is-numeric adm-text-danger">{money(i.amountDue)}</td>
                <td>{i.attempts}</td>
                <td>{i.nextAttemptAt ? formatDate(i.nextAttemptAt) : i.status === "uncollectible" ? "uncollectible" : "—"}</td>
                <td>{i.url && <a className="adm-link" href={i.url} target="_blank" rel="noopener noreferrer">Open in Stripe</a>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Logins ───────────────────────────────────────────────────────────────────

function LoginsTab({ auth }) {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(null);
  const [onlyFailed, setOnlyFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/admin/login-log?limit=200", { headers: { Authorization: "Bearer " + auth.token }, signal: controller.signal })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
        setEntries(data.entries || []);
      })
      .catch((err) => { if (err.name !== "AbortError") setError(err.message); });
    return () => controller.abort();
  }, [auth.token]);

  const rows = useMemo(() => (entries || []).filter((e) => !onlyFailed || e.status === "failed"), [entries, onlyFailed]);
  if (error) return <p className="adm-note adm-text-danger">Couldn't load the login log: {error}</p>;
  if (!entries) return <Skeleton className="adm-skeleton--row" />;

  const exportCsv = () => downloadCsv("mtt-logins.csv", ["Time", "Email", "Action", "Result", "Reason", "Device", "IP"],
    rows.map((e) => [e.timestamp, e.email, e.action, e.status, e.error, describeDevice(e.userAgent), e.ipAddress]));

  return (
    <>
      <div className="adm-list-head">
        <div className="adm-filters" role="group" aria-label="Filter logins">
          <button type="button" aria-pressed={!onlyFailed} className={`adm-filter ${!onlyFailed ? "is-active" : ""}`} onClick={() => setOnlyFailed(false)}>All <span>{entries.length}</span></button>
          <button type="button" aria-pressed={onlyFailed} className={`adm-filter ${onlyFailed ? "is-active" : ""}`} onClick={() => setOnlyFailed(true)}>Failed <span>{entries.filter((e) => e.status === "failed").length}</span></button>
        </div>
        <CsvButton onClick={exportCsv} />
      </div>
      <div className="adm-table-wrap">
        <table className="adm-table adm-table--static">
          <thead><tr><th>Time</th><th>Account</th><th>Event</th><th>Device</th><th>IP</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="adm-empty">No login events.</td></tr>}
            {rows.map((e, i) => (
              <tr key={`${e.timestamp}-${i}`}>
                <td title={formatDateTime(e.timestamp)}>{formatRelative(e.timestamp)}</td>
                <td>{e.email}</td>
                <td>
                  <span className={e.status === "failed" ? "adm-text-danger" : e.action === "SIGNUP" ? "adm-text-accent" : ""}>
                    {e.action === "SIGNUP" ? "Signup" : e.status === "failed" ? "Failed login" : "Login"}
                  </span>
                  {e.error && <span className="adm-user__email">{e.error}</span>}
                </td>
                <td>{describeDevice(e.userAgent)}</td>
                <td className="adm-mono">{e.ipAddress || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="adm-note">The latest {entries.length} login and signup events.</p>
    </>
  );
}

// ─── Section ──────────────────────────────────────────────────────────────────

const AdminDetails = ({ auth, onOpenUser }) => {
  const [tab, setTab] = useState("revenue");
  const [finance, setFinance] = useState(null);
  const [error, setError] = useState(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    fetch(`/api/admin/finance${refresh ? "?refresh=1" : ""}`, { headers: { Authorization: "Bearer " + auth.token }, signal: controller.signal })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
        setFinance(data);
      })
      .catch((err) => { if (err.name !== "AbortError") setError(err.message); });
    return () => controller.abort();
  }, [auth.token, refresh]);

  return (
    <section className="adm-panel adm-details" aria-label="Details">
      <div className="adm-details__head">
        <h2 className="adm-panel__title">Details</h2>
        <nav className="adm-tabs adm-tabs--small" aria-label="Detail lists">
          {TABS.map((t) => (
            <button key={t.key} type="button" aria-current={tab === t.key ? "page" : undefined} className={tab === t.key ? "is-active" : ""} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </nav>
        {tab !== "logins" && finance && (
          <span className="adm-note adm-details__meta">
            {finance.source === "stripe" ? "Stripe" : "Payment log"} · {formatRelative(finance.generatedAt)}
            <button type="button" className="adm-link adm-refresh" onClick={() => setRefresh((n) => n + 1)}>Refresh</button>
          </span>
        )}
      </div>

      {tab !== "logins" && finance?.warning && <p className="adm-note adm-text-warn">{finance.warning}</p>}
      {tab === "logins" ? <LoginsTab auth={auth} />
        : error ? <p className="adm-note adm-text-danger" role="alert">Couldn't load the finance data: {error}</p>
        : !finance ? <div aria-busy="true"><Skeleton className="adm-skeleton--chart" /></div>
        : tab === "revenue" ? <RevenueTab finance={finance} />
        : tab === "payments" ? <PaymentsTab finance={finance} auth={auth} onOpenUser={onOpenUser} />
        : <RenewalsTab finance={finance} onOpenUser={onOpenUser} />}
    </section>
  );
};

export default AdminDetails;
